import { SourceRoute } from '@/types';

/**
 * Vendor fields the outbound route view reads. A missing source_routes key
 * is not an empty list. Flat source_endpoint_id stays the inbound destination
 * even when this returns routes from the flat fields.
 */
export interface SourceRouteInput {
  source_routes?: unknown;
  source_endpoint_id?: string | null;
  source_folder?: string | null;
  file_pattern?: string | null;
}

function blank(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim();
}

function copyEmailList(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const addresses = value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
  return addresses.length > 0 ? addresses : undefined;
}

/** One stored route. Email keys are copied only when the list is non-empty. */
export function copyStoredRoute(route: unknown): SourceRoute {
  const record = route && typeof route === 'object'
    ? route as Record<string, unknown>
    : {};
  const copied: SourceRoute = {
    endpoint_id: typeof record.endpoint_id === 'string' ? record.endpoint_id : '',
    source_folder: typeof record.source_folder === 'string' ? record.source_folder : '',
    file_pattern: typeof record.file_pattern === 'string' ? record.file_pattern : '',
    active: record.active === true,
  };
  const successEmail = copyEmailList(record.success_email);
  const failureEmail = copyEmailList(record.failure_email);
  if (successEmail) {
    copied.success_email = successEmail;
  }
  if (failureEmail) {
    copied.failure_email = failureEmail;
  }
  return copied;
}

/** True only when source_routes is a non-empty array. */
export function hasStoredSourceRoutes(vendor: SourceRouteInput | null | undefined): boolean {
  return Array.isArray(vendor?.source_routes) && vendor.source_routes.length > 0;
}

/**
 * Same route list the backend uses for outbound pickup.
 * A non-empty source_routes array is copied as stored. Otherwise one route
 * is built from the three flat fields when each is non-blank, with active
 * true. Anything else is an empty list. The input is not modified.
 */
export function getSourceRoutes(vendor: SourceRouteInput | null | undefined): SourceRoute[] {
  if (hasStoredSourceRoutes(vendor)) {
    return (vendor?.source_routes as unknown[]).map(copyStoredRoute);
  }

  const endpointId = blank(vendor?.source_endpoint_id);
  const folder = blank(vendor?.source_folder);
  const pattern = blank(vendor?.file_pattern);
  if (!endpointId || !folder || !pattern) {
    return [];
  }
  return [{
    endpoint_id: endpointId,
    source_folder: folder,
    file_pattern: pattern,
    active: true,
  }];
}
